import { useEffect } from 'react';
import { THEME } from '@/common/styles/theme';

const MOBILE_MEDIA_QUERY = `(max-width: ${THEME.mobileBreakpoint})`;
const VIEWPORT_THRESHOLD = 0.92;
const KEYBOARD_BLUR_DELAY_MS = 120;

const applyViewportHeight = () => {
	if (typeof window === 'undefined') {
		return;
	}

	const viewport = window.visualViewport;
	const height = viewport?.height ?? window.innerHeight;

	document.documentElement.style.setProperty(
		'--app-height',
		`${Math.round(height)}px`,
	);
};

const resetScrollPosition = () => {
	window.scrollTo(0, 0);
	document.body.scrollTop = 0;
	document.documentElement.scrollTop = 0;
};

const enableViewportTracking = () => {
	applyViewportHeight();
};

const disableViewportTracking = () => {
	document.documentElement.style.removeProperty('--app-height');
	resetScrollPosition();
};

export const useMobileViewportHeight = () => {
	useEffect(() => {
		if (typeof window === 'undefined') {
			return undefined;
		}

		const setupViewportTracking = () => {
			const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

			if (!mediaQuery.matches) {
				return null;
			}

			enableViewportTracking();

			let rafId = 0;
			const viewport = window.visualViewport;

			const handleViewportChange = () => {
				cancelAnimationFrame(rafId);
				rafId = requestAnimationFrame(() => {
					if (!mediaQuery.matches) {
						return;
					}

					applyViewportHeight();

					if (
						viewport &&
						viewport.height >= window.innerHeight * VIEWPORT_THRESHOLD
					) {
						resetScrollPosition();
					}
				});
			};

			const handleMediaChange = (event) => {
				if (event.matches) {
					enableViewportTracking();
					return;
				}

				disableViewportTracking();
			};

			const handleFocusOut = (event) => {
				const target = event.target;

				if (
					!(target instanceof HTMLInputElement) &&
					!(target instanceof HTMLTextAreaElement)
				) {
					return;
				}

				window.setTimeout(handleViewportChange, KEYBOARD_BLUR_DELAY_MS);
			};

			viewport?.addEventListener('resize', handleViewportChange);
			viewport?.addEventListener('scroll', handleViewportChange);
			window.addEventListener('resize', handleViewportChange);
			window.addEventListener('orientationchange', handleViewportChange);
			mediaQuery.addEventListener('change', handleMediaChange);
			document.addEventListener('focusout', handleFocusOut);

			return () => {
				cancelAnimationFrame(rafId);
				viewport?.removeEventListener('resize', handleViewportChange);
				viewport?.removeEventListener('scroll', handleViewportChange);
				window.removeEventListener('resize', handleViewportChange);
				window.removeEventListener('orientationchange', handleViewportChange);
				mediaQuery.removeEventListener('change', handleMediaChange);
				document.removeEventListener('focusout', handleFocusOut);
				disableViewportTracking();
			};
		};

		const cleanup = setupViewportTracking();
		return cleanup || undefined;
	}, []);
};

export const resetMobileViewportAfterKeyboard = () => {
	if (typeof window === 'undefined') {
		return;
	}

	window.setTimeout(() => {
		applyViewportHeight();
		resetScrollPosition();
	}, KEYBOARD_BLUR_DELAY_MS);
};
